import { useEffect, useMemo } from "react"
import { useParams } from "react-router-dom"
import { setDocumentTitle } from "shared/util"

import { Box, Container, Paper, Stack, useMediaQuery, useTheme } from "@mui/material"
import { Alert, StackProps } from "@mui/material"

import { ErrorBlock } from "components/controls/errorInfo"
import { Loading } from "components/controls/loading"
import { useSideMenu } from "components/sideMenu/container"
import { useTopBar } from "components/topBar/container"
import { BreadCrumbItem } from "components/topBar/types"

import { SearchProvider } from "features/search/context"
import { useTenant } from "features/tenant/context"

import { useGetSearchDetail } from "interface/search"
import { SisetuResult, useGetSisetu } from "interface/sisetu"

import { SisetuPreview } from "./preview"

type SisetuPreviewPageCoreProps = {
  sisetu: SisetuResult
}
export const SisetuPreviewPageCore = ({
  sisetu
}: SisetuPreviewPageCoreProps) => {
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down("sm"))
  return (
    <Box
      flexGrow={1}
      boxSizing="border-box"
      width="100%"
      overflow="hidden"
      position="relative"
    >
      <Container
        maxWidth={isSmallMedia ? undefined : "xl"}
        sx={{
          height: '100%',
          padding: isSmallMedia ? 0 : 2
        }}
      >
        <Paper
          square={isSmallMedia}
          sx={{
            height: "100%",
            width: "100%",
            overflow: "hidden"
          }}
        >
          <Stack direction="column" height='100%' overflow='hidden'>
            <SisetuPreview sisetu={sisetu}/>
          </Stack>
        </Paper>
      </Container>
    </Box>
  )
}

type SisetuPreviewPageProps = {
  searchId?: number
  topBarState: ReturnType<typeof useTopBar>
  sideMenuState: ReturnType<typeof useSideMenu>
} & StackProps
export const SisetuPreviewPage = ({
	searchId,
	topBarState: { setBreadCrumbs },
	sideMenuState: { setActivePage }
}: SisetuPreviewPageProps) => {
  const tenant = useTenant()
	const { sisetuCode } = useParams()
  const searchState = useGetSearchDetail(searchId)
  const sisetuState = useGetSisetu(searchId, sisetuCode)
  const error = useMemo(
    () => [searchState, sisetuState].flatMap(state => state.error ?? []),
    [searchState, sisetuState]
  )
  useEffect(
    () => {
			const search = searchState.data
      const sisetu = sisetuState.data
      setDocumentTitle(
        sisetu?.sisetuName ?? undefined,
        search?.title,
        tenant.tenantName
      )
      const crumbs: BreadCrumbItem[] = []
      if (search?.manufacturer?.manufacturerName) {
        crumbs.push({
          label: search.manufacturer.manufacturerName,
          url: `/${tenant.domain}/search/list#manufacturer-${search.manufacturer.manufacturerId}`
        })
      }
			if (search) {
				crumbs.push({
					label: search.title,
					url: `/${tenant.domain}/search/${search.searchId}/home`
				})
      }
      if (sisetu) {
        crumbs.push({
          label: sisetu.sisetuName ?? sisetu.sisetuNameKana ?? 'no name'
        })
      }
      setBreadCrumbs(crumbs)
      setActivePage('sisetu')
    },
    [tenant, searchState, sisetuState, setBreadCrumbs, setActivePage]
  )
  if (!sisetuCode) return <Alert severity="error" sx={{ wordBreak: 'break-all' }}>パラメータ不正</Alert>
  if (searchState.loading || sisetuState.loading) return <Loading/>
  if (error.length > 0) return <ErrorBlock error={error}/>
  if (!sisetuState.data) return <></>

	return (
    <SearchProvider search={searchState.data}>
			<SisetuPreviewPageCore sisetu={sisetuState.data}/>
		</SearchProvider>
	)
}